import axios, { AxiosError, HttpStatusCode } from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import type { AxiosResponse } from 'axios'
import { getAppUrl, logOut } from '@/utils'
import { notification } from 'ant-design-vue'
import type { ResponseBody } from '@/types'
import { getAuthInfo, logout } from '@/apis/userInfoService.ts'
import { useAuthStore } from '@/stores/authStore.ts'

type RetryableRequest = InternalAxiosRequestConfig & { _retry?: boolean }

// Singleton refresh: khi nhiều request business cùng 401 trong một "nháy",
// chúng sẽ cùng await một lời gọi /api/auth/info thay vì mỗi request tự gọi
// một lần. Tránh spam BFF và tránh race với refresh_token one-time-use.
let refreshPromise: Promise<Awaited<ReturnType<typeof getAuthInfo>>> | null = null
function sharedRefresh() {
  if (!refreshPromise) {
    refreshPromise = getAuthInfo().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

export const httpClient: AxiosInstance = axios.create({
  baseURL: `${getAppUrl()}/api/cop`,
  timeout: 600000,
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
    'X-App-Code': import.meta.env.VITE_CLIENT_ID
  },
  withCredentials: true
})

httpClient.interceptors.request.use(
  function (config) {
    const { auth } = useAuthStore()
    if (auth.accessToken) {
      config.headers['Authorization'] = `Bearer ${auth.accessToken}`
    }
    return config
  },
  function (error) {
    return Promise.reject(error)
  }
)
async function errorHandler(error: AxiosError): Promise<any> {
  const originalRequest = error.config as RetryableRequest | undefined
  console.log('error response: ', error.response)
  if (error.response) {
    const { data, status, statusText } = error.response as AxiosResponse<ResponseBody>
    const isAuthInfoUrl =
      originalRequest?.url === '/api/auth/info' ||
      originalRequest?.url === `${getAppUrl()}/api/auth/info`
    // Nếu chính /api/auth/info bị lỗi thì silence ở đây, để caller (errorHandler
    // của request gốc) tự xử lý logout + notification — tránh double-notify.
    if (isAuthInfoUrl) {
      return Promise.reject(error)
    }
    if (status === HttpStatusCode.Unauthorized && originalRequest) {
      // Đã retry một lần sau refresh mà vẫn 401 → client/server mất đồng bộ
      // thực sự (session chết hoặc token không còn hợp lệ). Logout.
      if (originalRequest._retry) {
        await logout()
        notification?.error({
          message: '401',
          description: 'Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại',
          duration: 3
        })
        logOut()
        return Promise.reject(error)
      }
      originalRequest._retry = true
      const res = await sharedRefresh()
      const authStore = useAuthStore()
      authStore.setAccessToken(res.accessToken)
      if (res.refreshToken) authStore.setRefreshToken(res.refreshToken)
      originalRequest.headers = originalRequest.headers ?? {}
      originalRequest.headers['Authorization'] = `Bearer ${res.accessToken}`
      return httpClient.request(originalRequest)
    } else if (status === HttpStatusCode.Forbidden) {
      notification?.error({
        message: '403',
        description: data?.message || statusText,
        duration: 3
      })
    } else if (status === HttpStatusCode.InternalServerError) {
      notification?.error({
        message: '500',
        description: data?.message || statusText,
        duration: 3
      })
    } else {
      notification?.error({
        message: 'Thông báo',
        description: data?.message || statusText,
        duration: 3
      })
    }
  }
  return Promise.reject(error)
}
httpClient.interceptors.response.use(function (response) {
  return response
}, errorHandler)
